import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { isTokenValid, removeToken } from '../../utils/Auth';
import { sharedStyles } from '../../utils/Style';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { useSelfAffiliateCode } from '../../hooks/useSelfAffiliateCode';

const DummyScrollButton = () => <></>;

export type AccountTab =
  | 'panel'
  | 'partner-program'
  | 'profile'
  | 'orders'
  | 'refunds'
  | 'address'
  | 'password'
  | 'logout';

type AccountNavbarProps = {
  selectedTab: AccountTab;
};

export const AccountNavbar = ({ selectedTab }: AccountNavbarProps) => {
  const t = useLocalizedText();
  const { refresh: refreshSelfAffiliateCode, selfAffiliateCode } =
    useSelfAffiliateCode();

  const navigate = useNavigate();

  useEffect(() => {
    if (!isTokenValid()) {
      navigate('/giris-yap');
    }
  }, []);

  const tabNames: Record<AccountTab, string> = {
    panel: t('account.tabs.panel'),
    'partner-program': t('account.tabs.affiliate_program'),
    profile: t('account.tabs.profile'),
    orders: t('account.tabs.orders'),
    refunds: t('account.tabs.refunds'),
    address: t('account.tabs.addresses'),
    password: t('account.tabs.password'),
    logout: t('account.tabs.logout'),
  };

  const availableTabs: AccountTab[] = [
    'panel',
    ...(selfAffiliateCode ? ['partner-program' as const] : []),
    'profile',
    'orders',
    'refunds',
    'address',
    'password',
    'logout',
  ];

  const handleChange = useCallback((newValue: AccountTab) => {
    switch (newValue) {
      case 'panel': {
        navigate('/hesabim');
        break;
      }
      case 'partner-program': {
        navigate('/hesabim/partner-programi');
        break;
      }
      case 'profile': {
        navigate('/hesabim/profil-duzenle');
        break;
      }
      case 'orders': {
        navigate('/hesabim/siparislerim');
        break;
      }
      case 'refunds': {
        navigate('/hesabim/iadelerim');
        break;
      }
      case 'address': {
        navigate('/hesabim/adres');
        break;
      }
      case 'password': {
        navigate('/hesabim/sifre');
        break;
      }
      case 'logout': {
        removeToken();
        refreshSelfAffiliateCode();
        navigate('/');
        break;
      }
      default: {
        navigate('/hesabim');
        break;
      }
    }
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Tabs
        variant='scrollable'
        value={selectedTab}
        onChange={(_, value) => handleChange(value)}
        ScrollButtonComponent={DummyScrollButton}
      >
        {availableTabs.map((tab) => (
          <Tab
            key={tab}
            value={tab}
            label={
              <Typography sx={sharedStyles.tabLabel}>
                {tabNames[tab]}
              </Typography>
            }
          />
        ))}
      </Tabs>

      <Divider />
    </Box>
  );
};
